import axios from 'axios';
import apiClient from '../config/axiosConfig';
import { initializeAuthService, getAuthService } from '../services/authServiceInitializer';
import { API_BASE_URL } from '../utils/apiUtils';

const validateEmail = (email) => {
  if (!email || typeof email !== 'string' || email.trim() === '') {
    throw new Error('Email is required and must be a valid string');
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    throw new Error('Please enter a valid email address');
  }

  return email.trim().toLowerCase();
};

const validatePassword = (password) => {
  if (!password || typeof password !== 'string' || password.trim() === '') {
    throw new Error('Password is required and must be a valid string');
  }

  if (password.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }

  return password;
};

const validateFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string' || fullName.trim() === '') {
    throw new Error('Full name is required and must be a valid string');
  }

  if (fullName.trim().length < 2) {
    throw new Error('Full name must be at least 2 characters long');
  }

  return fullName.trim();
};

const validateUserId = (userId) => {
  if (!userId || typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('User ID is required and must be a valid string');
  }
  return userId.trim();
};

const validateIdToken = (idToken) => {
  if (!idToken || typeof idToken !== 'string' || idToken.trim() === '') {
    throw new Error('ID token is required and must be a valid string');
  }
  return idToken.trim();
};

const validateAccessToken = (accessToken) => {
  if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
    throw new Error('Access token is required and must be a valid string');
  }
  return accessToken.trim();
};

const validateStringValue = (value, fieldName) => {
  if (value !== null && value !== undefined && typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  return value;
};

const setupAuthService = async () => {
  await initializeAuthService();
};

const handleAuthorizationError = (error, operation = 'API operation') => {
  console.error(`[AuthAPI] ${operation} Error:`, error.message);

  // Handle specific error cases
  if (error.response?.status === 401) {
    // Token expired or invalid - clear local storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    throw new Error('Your session has expired. Please log in again');
  }

  if (error.response?.status === 403) {
    throw new Error('You do not have permission to perform this action');
  }

  if (error.response?.status === 404) {
    throw new Error('User not found or resource does not exist');
  }

  if (error.response?.status === 409) {
    throw new Error('Resource already exists or conflicts with existing data');
  }

  if (error.response?.status === 422) {
    const validationErrors = error.response?.data?.errors || [];
    const errorMessage = validationErrors.length > 0
      ? `Validation errors: ${validationErrors.join(', ')}`
      : 'Invalid data provided';
    throw new Error(errorMessage);
  }

  if (error.response?.status >= 500) {
    throw new Error('Server error. Please try again later.');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  // Default error message
  const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
  throw new Error(errorMessage);
};

export const loginUser = async (userEmail, userPassword) => {
  try {
    const validEmail = validateEmail(userEmail);
    const validPassword = validatePassword(userPassword);

    await setupAuthService();
    const authService = getAuthService();

    const userExists = await checkUserExists(validEmail);

    if (!userExists) {
      return {
        success: false,
        code: 'auth/user-not-found',
        message: 'No account found with this email. Please sign up first.'
      };
    }

    try {
      const userCredential = await authService.signInUser(validEmail, validPassword);
      const user = userCredential.user;
      const userId = user.uid;

      const idToken = await user.getIdToken(true);

      try {
        const response = await apiClient.get(`${API_BASE_URL}/api/auth/user/${userId}`);

        if (response.data.code === 'account/pending-deletion') {
          return {
            success: false,
            code: 'account/pending-deletion',
            message: 'Your account is currently being deleted. This process may take up to 24 hours to complete.'
          };
        }

        if (!response.data.data.isActive) {
          return {
            success: false,
            code: 'account/inactive',
            message: 'Your account is inactive. Please contact support for assistance.'
          };
        }

        localStorage.setItem('authToken', idToken);
        localStorage.setItem('authUser', JSON.stringify({
          ...response.data.data,
          idToken
        }));

        return {
          success: true,
          emailVerified: user.emailVerified,
          user: response.data.data,
          idToken,
          registered: true
        };
      } catch (apiError) {
        handleAuthorizationError(apiError, 'Fetching user data');

        if (apiError.response?.data?.code === 'account/pending-deletion') {
          return {
            success: false,
            code: 'account/pending-deletion',
            message: 'Your account is currently being deleted. This process may take up to 24 hours to complete.'
          };
        }

        throw {
          code: 'api/error',
          message: 'Error fetching user data. Please try again or contact support if the issue persists.'
        };
      }
    } catch (authError) {
      // Handle Firebase authentication errors
      if (authError.code === 'auth/email-not-verified') {
        return {
          success: false,
          code: 'auth/email-not-verified',
          message: 'Please verify your email before signing in. Check your inbox for the verification link.'
        };
      }

      if (authError.code === 'auth/invalid-credential') {
        return {
          success: false,
          code: 'auth/invalid-credential',
          message: 'Invalid email or password. Please try again.'
        };
      }

      if (authError.code === 'auth/too-many-requests') {
        return {
          success: false,
          code: 'auth/too-many-requests',
          message: 'Too many failed attempts. Please try again later or reset your password.'
        };
      }

      throw authError;
    }
  } catch (error) {
    return {
      success: false,
      code: error.code || 'auth/unknown-error',
      message: error.message || 'An error occurred during sign in. Please try again.'
    };
  }
};

export const reauthenticateUser = async (email, password) => {
  try {
    const validEmail = validateEmail(email);
    const validPassword = validatePassword(password);

    await setupAuthService();
    const authService = getAuthService();

    await authService.reauthenticateWithCredential(validEmail, validPassword);

    return {
      success: true,
      message: 'Authentication successful'
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Reauthenticating user');
    }

    throw {
      code: error.code || 'auth/unknown',
      message: error.message || 'Failed to reauthenticate',
      requiresReauth: false
    };
  }
};

export const reauthenticateWithGoogle = async () => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    const result = await authService.reauthenticateWithGoogle();

    return {
      success: true,
      user: result.user,
      message: 'Google authentication successful'
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Reauthenticating with Google');
    }

    throw {
      code: error.code || 'auth/unknown',
      message: error.message || 'Failed to reauthenticate with Google',
      requiresReauth: false
    };
  }
};

export const deleteUserAccount = async () => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    const user = await authService.getCurrentUser();
    const userId = user.uid;

    try {
      const idToken = await user.getIdToken(true);

      try {
        await authService.deleteCurrentUser();
      } catch (error) {
        if (error.code === 'auth/requires-recent-login' || error.requiresReauth) {
          throw {
            code: 'auth/requires-recent-login',
            message: 'Please sign in again to delete your account',
            requiresReauth: true
          };
        }
        throw error;
      }

      const response = await apiClient.delete(`${API_BASE_URL}/api/auth/user/${userId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete MongoDB data');
      }

      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');

      return {
        success: true,
        message: 'Account successfully deleted'
      };

    } catch (error) {
      if (error.response) {
        handleAuthorizationError(error, 'Deleting user account');
      }

      // Handle specific errors
      if (error.code === 'auth/requires-recent-login' || error.requiresReauth) {
        throw {
          code: 'auth/requires-recent-login',
          message: 'Please sign in again to delete your account',
          requiresReauth: true
        };
      }

      throw new Error(error.response?.data?.message || error.message || 'Failed to delete account');
    }
  } catch (error) {
    throw {
      code: error.code || 'unknown',
      message: error.message || 'Failed to delete account',
      requiresReauth: error.requiresReauth || false
    };
  }
};

export const queueAccountForDeletion = async () => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    const user = await authService.getCurrentUser();
    const userId = user.uid;
    const idToken = await user.getIdToken(true);

    const response = await apiClient.post(
      `${API_BASE_URL}/api/deletion-queue/queue/${userId}`,
      {}
    );

    return {
      success: true,
      message: response.data.message,
      queuedAt: response.data.queuedAt
    };
  } catch (error) {
    handleAuthorizationError(error, 'Queueing account for deletion');

    // Check for authentication issues
    if (error.response?.status === 401 ||
      error.code === 'auth/requires-recent-login') {
      throw {
        code: 'auth/requires-recent-login',
        message: 'Please sign in again to delete your account',
        requiresReauth: true
      };
    }

    // Handle other errors
    throw {
      code: error.code || 'unknown',
      message: error.response?.data?.message || error.message || 'Failed to queue account for deletion',
      requiresReauth: false
    };
  }
};

export const resendVerificationEmail = async (email) => {
  try {
    const validEmail = validateEmail(email);

    await setupAuthService();
    const authService = getAuthService();

    const currentUser = await authService.getCurrentUser();
    const result = await authService.sendVerificationEmail(currentUser);

    return {
      success: true,
      message: 'Verification email resent successfully'
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Resending verification email');
    }

    return {
      success: false,
      message: error.message || 'Failed to resend verification email'
    };
  }
};

export const forgotPassword = async (email) => {
  try {
    const validEmail = validateEmail(email);

    await setupAuthService();
    const authService = getAuthService();

    const result = await authService.forgotPassword(validEmail);

    return {
      success: true,
      message: 'Password reset email sent successfully'
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Sending password reset email');
    }

    return {
      success: false,
      message: error.message || 'Failed to send password reset email'
    };
  }
};

export const checkUserExists = async (email) => {
  try {
    const validEmail = validateEmail(email);

    const response = await apiClient.get(`${API_BASE_URL}/api/auth/check-email`, {
      params: { email: validEmail }
    });

    return response.data.exists;
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Checking user existence');
    }

    return false;
  }
};

export const registerUser = async (userEmail, userPassword, fullName) => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    localStorage.setItem('signupInProgress', 'true');

    const validEmail = validateEmail(userEmail);
    const validPassword = validatePassword(userPassword);
    const validFullName = validateFullName(fullName);

    const userExists = await checkUserExists(validEmail);
    if (userExists) {
      localStorage.removeItem('signupInProgress');
      return {
        success: false,
        code: 'auth/email-already-in-use',
        message: 'An account with this email already exists. Please sign in instead.'
      };
    }

    const userCredential = await authService.signUpUser(validEmail, validPassword);
    const userId = userCredential.user.uid;

    try {
      const response = await apiClient.post(`${API_BASE_URL}/api/auth/register`, {
        userEmail: validEmail,
        fullName: validFullName,
        userId
      });

      console.log('🔍 SignUp: Backend registration successful');

      try {
        const refreshedToken = await userCredential.user.getIdToken(true);
        localStorage.setItem('authToken', refreshedToken);
        console.log('🔍 SignUp: Token refreshed after registration');
      } catch (tokenError) {
        console.warn('⚠️ SignUp: Could not refresh token after registration:', tokenError.message);
      }

      console.log('🔍 SignUp: Sending verification email...');
      try {
        await authService.sendVerificationEmail(userCredential.user);
        console.log('✅ SignUp: Verification email sent successfully');
      } catch (verifyError) {
        console.error('❌ SignUp: Failed to send verification email:', verifyError.message);
        // We don't necessarily want to fail registration if only the email fails,
        // but the user reported not receiving it, so we should at least let them know.
      }

      localStorage.removeItem('signupInProgress');

      return {
        success: true,
        user: response.data.user,
        message: 'Registration successful. Please check your email to verify your account.'
      };
    } catch (error) {
      console.error('❌ SignUp: Error during backend registration or verification:', error);
      if (error.response) {
        handleAuthorizationError(error, 'Registering user in backend');
      }

      try {
        await authService.deleteCurrentUser();
      } catch (deleteError) {
        // Cleanup failed, continue
      }

      localStorage.removeItem('signupInProgress');

      throw {
        code: 'auth/registration-failed',
        message: 'Registration failed. Please try again or contact support if the issue persists.'
      };
    }
  } catch (error) {
    localStorage.removeItem('signupInProgress');

    return {
      success: false,
      code: error.code || 'auth/unknown-error',
      message: error.message || 'An error occurred during registration. Please try again.'
    };
  }
};

export const googleSignIn = async () => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    const { idToken, user } = await authService.signInWithGoogle();
    const email = user.email;

    const userExists = await checkUserExists(email);

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Google sign-in timeout after 15 seconds')), 15000);
    });

    const response = await Promise.race([
      apiClient.post(`${API_BASE_URL}/api/auth/google-signin`, {
        idToken
      }),
      timeoutPromise
    ]);

    if (response.data.code === 'account/pending-deletion') {
      return {
        success: false,
        code: 'account/pending-deletion',
        message: 'Your account is currently being deleted. This process may take up to 24 hours to complete.'
      };
    }

    localStorage.setItem('authToken', idToken);
    localStorage.setItem('authUser', JSON.stringify({
      ...response.data.user,
      idToken
    }));

    return {
      success: true,
      emailVerified: user.emailVerified,
      user: response.data.user,
      firebaseUser: user,
      idToken,
      registered: true
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Google sign-in');
    }

    if (error.response?.data?.code === 'account/pending-deletion') {
      return {
        success: false,
        code: 'account/pending-deletion',
        message: 'Your account is currently being deleted. This process may take up to 24 hours to complete.'
      };
    }

    return {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred during Google sign-in'
    };
  }
};

export const googleSignUp = async () => {
  try {
    await setupAuthService();
    const authService = getAuthService();

    const { idToken, user } = await authService.signInWithGoogle();

    const userExists = await checkUserExists(user.email);

    if (userExists) {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Google sign-in timeout after 15 seconds')), 15000);
      });

      const response = await Promise.race([
        apiClient.post(`${API_BASE_URL}/api/auth/google-signin`, {
          idToken
        }),
        timeoutPromise
      ]);

      localStorage.setItem('authToken', idToken);
      localStorage.setItem('authUser', JSON.stringify({
        ...response.data.user,
        idToken
      }));

      return {
        success: true,
        emailVerified: user.emailVerified,
        user: response.data.user,
        firebaseUser: user,
        idToken,
        registered: true,
        isNewUser: false
      };
    }

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Google sign-up timeout after 30 seconds')), 30000);
    });

    const response = await Promise.race([
      apiClient.post(`${API_BASE_URL}/api/auth/google-signup`, {
        idToken
      }),
      timeoutPromise
    ]);

    try {
      const refreshedToken = await user.getIdToken(true);
      localStorage.setItem('authToken', refreshedToken);
    } catch (tokenError) {
      // Use original token if refresh fails
    }

    localStorage.setItem('authToken', idToken);
    localStorage.setItem('authUser', JSON.stringify({
      ...response.data.user,
      idToken
    }));

    return {
      success: true,
      emailVerified: user.emailVerified,
      user: response.data.user,
      firebaseUser: user,
      idToken,
      registered: true,
      isNewUser: true
    };
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Google sign-up');
    }

    return {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred during Google sign-up'
    };
  }
};

export const facebookLogin = async (accessToken) => {
  try {
    const validAccessToken = validateAccessToken(accessToken);

    await setupAuthService();
    const authService = getAuthService();

    const { idToken } = await authService.signInWithFacebook(validAccessToken);

    const response = await apiClient.post(`${API_BASE_URL}/api/auth/facebook-login`, {
      accessToken: validAccessToken
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      handleAuthorizationError(error, 'Facebook login');
    }

    throw error;
  }
};

export const getUserById = async (userId, idToken = null) => {
  const validUserId = validateUserId(userId);

  const validIdToken = idToken ? validateIdToken(idToken) : null;

  const maxRetries = 3;
  const baseDelay = 1000;
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const requestConfig = {
        method: 'GET',
        url: `${API_BASE_URL}/api/auth/user/${validUserId}`,
        headers: {}
      };

      if (validIdToken) {
        requestConfig.headers.Authorization = `Bearer ${validIdToken}`;
      }

      const response = await apiClient(requestConfig);

      return response.data;
    } catch (error) {
      lastError = error;

      const isUserNotFound = error.response?.status === 404;
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNetworkError = error.code === 'ERR_NETWORK';

      if (attempt === maxRetries) {
        handleAuthorizationError(error, 'Getting user by ID');

        if (error.response) {
          throw new Error(error.response.data.message || 'An error occurred');
        } else if (error.request) {
          throw new Error('No response received from the server. Please check your network connection.');
        } else {
          throw new Error('An error occurred: ' + error.message);
        }
      }

      if (isUserNotFound || isTimeout || isNetworkError) {
        const delay = baseDelay * attempt;
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        break;
      }
    }
  }

  throw lastError;
};

export const logoutUser = () => {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    sessionStorage.clear();

    return {
      success: true,
      message: 'Logged out successfully'
    };
  } catch (error) {
    return {
      success: false,
      message: 'Error during logout'
    };
  }
};

export const updateUserDefaultSettings = async (userId, settings) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.put(`${API_BASE_URL}/api/user/${validUserId}/default-settings`, settings);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to update default settings with an unexpected status code');
    }
  } catch (error) {
    throw error;
  }
};

export const getUserDefaultSettings = async (userId) => {
  try {
    const validUserId = validateUserId(userId);

    const response = await apiClient.get(`${API_BASE_URL}/api/user/${validUserId}/default-settings`);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to get default settings with an unexpected status code');
    }
  } catch (error) {
    throw error;
  }
};